import useModals from '@/composables/modals.js'
import { useToastNotifications } from '@/composables/toastNotifications.js'
import { useToastNotificationsStore } from '@/stores/toastNotifications.js'
import useAnalytics from '@shared/composables/analytics.js'
import { useClientStore } from '@shared/stores/client.js'
import { whenever } from '@vueuse/core'
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useNow } from '@vueuse/core'

export const useOnboardingStore = defineStore('onboarding', () => {
  const { showOnboardingToast } = useToastNotifications()
  const { dismissToastNotification } = useToastNotificationsStore()
  const {
    openUnlimitedIntroModal,
    closeUnlimitedIntroModal,
    openHowToSwapModal,
    closeHowToSwapModal
  } = useModals()
  const { trackOverlayInteraction } = useAnalytics()
  const route = useRoute()

  const clientStore = useClientStore()
  const {
    membershipStatus,
    membershipStartDate,
    subscriptionStartDate,
    selectedPackages,
    completedUnlimitedIntro,
    shownHowToSwap
  } = storeToRefs(clientStore)
  const { updateClient } = clientStore

  const now = useNow({ interval: 60000 })

  const toastId = ref(null)
  const triggered = ref(false)

  const joinedAfterLaunch = computed(() =>
    membershipStartDate.value && membershipStartDate.value >= new Date('2025-06-23')
  )

  const eligible = computed(() =>
    membershipStatus?.value === 'active' &&
    joinedAfterLaunch.value &&
    selectedPackages?.value < 2
  )

  const completed = computed(() => completedUnlimitedIntro.value === true)

  const howToSwapCompleted = computed(() => shownHowToSwap.value === true)

  const daysSinceSubscription = computed(() => {
    if (!subscriptionStartDate.value) return 0
    const diffMs = now.value - subscriptionStartDate.value
    return Math.floor(diffMs / (1000 * 60 * 60 * 24))
  })

  const shouldShowHowToSwap = computed(() =>
    eligible.value &&
    completed.value &&
    !howToSwapCompleted.value &&
    daysSinceSubscription.value >= 6
  )

  const toastMessage = computed(() => ({
    text: 'How To Swap',
    bold: true
  }))

  function onMyClosetRoute(callback) {
    whenever(() => route.name === 'closet-sections', callback, {
      immediate: true,
      once: true,
    })
  }

  function triggerOnboardingFlow() {
    if (triggered.value) {
      return
    }
    triggered.value = true

    onMyClosetRoute(() => {
      if (toastId.value === null) {
        toastId.value = showOnboardingToast(toastMessage, openHowToSwapModal)
      }
      if (!completed.value) {
        openUnlimitedIntroModal()
      } else if (shouldShowHowToSwap.value) {
        openHowToSwapModal()
        completeHowToSwap()
      }
    })
  }

  function dismissOnboardingFlow() {
    closeUnlimitedIntroModal()
    if (toastId.value !== null) {
      dismissToastNotification(toastId.value)
      toastId.value = null
    }
  }

  async function completeUnlimitedIntro() {
    closeUnlimitedIntroModal()
    if (!completedUnlimitedIntro.value) {
      await updateClient({ completedUnlimitedIntro: true })
    }
  }

  async function completeHowToSwap() {
    if (!shownHowToSwap.value) {
      await updateClient({ shownHowToSwap: true })
    }
  }

  function trackUnlimitedIntro(action, data = {}) {
    trackOverlayInteraction('unlimited-intro', 'modal', action, {
      completed: completed.value,
      ...data
    })
  }

  function trackHowToSwap(action, data = {}) {
    trackOverlayInteraction('how-to-swap', 'modal', action, {
      completed: howToSwapCompleted.value,
      daysSinceSubscription: daysSinceSubscription.value,
      ...data
    })
  }

  return {
    toastId,
    triggered,
    eligible,
    completed,
    howToSwapCompleted,
    daysSinceSubscription,
    shouldShowHowToSwap,
    toastMessage,
    triggerOnboardingFlow,
    dismissOnboardingFlow,
    completeUnlimitedIntro,
    completeHowToSwap,
    closeUnlimitedIntroModal,
    closeHowToSwapModal,
    trackUnlimitedIntro,
    trackHowToSwap,
  }
})

import.meta.hot?.accept(acceptHMRUpdate(useOnboardingStore, import.meta.hot))
